import { CLOUD_DATA_SCHEMA_NOTIFIER, WIX_CMS } from '@wix/app-definition-ids'
import type { Channel, VirtualSocket } from '@wix/duplexer-js'
import { Duplexer } from '@wix/duplexer-js'
import type {
  DataSettings,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import { createError } from '@wix/wix-data-client-common-standalone'
import type { SiteDataUsage } from '@wix/ambassador-cloud-data-usage-v1-site-data-usage/types'
import type { PageLinkGenerationPayload } from './onPageLinkGeneration'

interface SubscribeParams<T> {
  appDefId: string
  channelName: string
  event: string
  listener: (payload: T) => void
}

type Connections = Record<
  string,
  {
    connection: VirtualSocket
    channels: Record<string, Channel>
  }
>

export interface DuplexerClient {
  onSchemaChange: (listener: () => Promise<void>) => void
  onPremiumLimitsChange: (
    listener: (payload: SiteDataUsage) => Promise<void>,
  ) => void
  onPageLinkGeneration: (
    listener: (payload: PageLinkGenerationPayload) => Promise<void>,
  ) => void
  onSlugValuesGenerated: (
    listener: (payload: {
      collectionId: string
      fields: string[]
    }) => Promise<void>,
  ) => void
  onDataSettingsChange: (
    listener: (payload: DataSettings & { changeType: string }) => Promise<void>,
  ) => void
}

interface Params {
  getInstance: () => string
  logger: Logger
}

export const createDuplexerClient = ({
  getInstance,
  logger,
}: Params): DuplexerClient => {
  const duplexer = new Duplexer('duplexer.wix.com', {
    instanceUpdater: {
      getInstance,
    },
  })

  const connections: Connections = {}

  const getChannel = (appDefId: string, channelName: string): Channel => {
    connections[appDefId] ??= {
      connection: duplexer.connect({ appDefId }),
      channels: {},
    }
    const { connection, channels } = connections[appDefId]
    channels[channelName] ??= connection.subscribe(channelName)
    return channels[channelName]
  }

  const subscribe = <T>({
    appDefId,
    channelName,
    event,
    listener,
  }: SubscribeParams<T>): void => {
    const channel = getChannel(appDefId, channelName)
    channel.on(event, async (payload: T) => {
      try {
        await listener(payload)
      } catch (error) {
        logger.error(
          createError('duplexer listener failed', {
            data: {
              appDefId,
              channelName,
              event,
            },
            cause: error,
          }),
        )
      }
    })
  }

  return {
    onSchemaChange: listener =>
      subscribe({
        appDefId: CLOUD_DATA_SCHEMA_NOTIFIER,
        channelName: 'wix-data-schema-change-notifications',
        event: 'schema-change',
        listener,
      }),
    onPremiumLimitsChange: listener =>
      subscribe({
        appDefId: WIX_CMS,
        channelName: 'wix-site-data-usage-notifications',
        event: 'limits-changed',
        listener,
      }),
    onPageLinkGeneration: listener =>
      subscribe({
        appDefId: WIX_CMS,
        channelName: 'wix-data-pagelink-rendering-notifications',
        event: 'pagelink-rendering-complete',
        listener,
      }),
    onSlugValuesGenerated: listener =>
      subscribe({
        appDefId: WIX_CMS,
        channelName: 'wix-data-pagelink-rendering-notifications',
        event: 'slug-rendering-complete',
        listener,
      }),
    onDataSettingsChange: listener =>
      subscribe({
        appDefId: CLOUD_DATA_SCHEMA_NOTIFIER,
        channelName: 'wix-data-settings-change-notifications',
        event: 'data-settings-change',
        listener,
      }),
  }
}
